import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TrendingUp,
  Package,
  Users,
  Mail,
  FileText,
  LogOut,
  RefreshCw,
  Calendar,
  CheckCircle,
  Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDkk(n) {
  return new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("da-DK", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatCard({ icon: Icon, label, value, sub, color = "primary" }) {
  const palette = {
    primary: "bg-primary/10 text-primary",
    secondary: "bg-secondary/20 text-secondary",
    green: "bg-accent/15 text-accent",
    pink: "bg-pink-100 text-pink-700",
    blue: "bg-sky-100 text-sky-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-border/40 shadow-sm p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground">
          {label}
        </span>
        <div className={`w-9 h-9 rounded-full ${palette[color]} flex items-center justify-center`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className="font-heading text-2xl md:text-3xl font-bold text-primary">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function Chart({ data }) {
  const max = Math.max(...data.map((d) => d.revenue), 1);
  return (
    <div className="bg-white rounded-2xl border border-border/40 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-primary">Omsætning sidste 14 dage</h3>
        <Calendar className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex items-end gap-1.5 h-32" data-testid="admin-chart">
        {data.map((d) => {
          const heightPct = (d.revenue / max) * 100;
          return (
            <div key={d.day} className="flex-1 flex flex-col items-center" title={`${d.day}: ${formatDkk(d.revenue)} (${d.orders} ordrer)`}>
              <div
                className="w-full bg-secondary/60 hover:bg-secondary rounded-t transition-colors"
                style={{ height: `${Math.max(heightPct, 2)}%` }}
              />
              <span className="text-[9px] text-muted-foreground mt-1 rotate-45 origin-left">
                {d.day.slice(5)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("orders");

  const token = typeof window !== "undefined" ? localStorage.getItem("mf_admin_token") : null;

  const headers = { Authorization: `Bearer ${token}` };

  const handleAuthError = useCallback(() => {
    localStorage.removeItem("mf_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  const fetchAll = useCallback(async () => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    setLoading(true);
    try {
      const [s, o, c, n, d] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/orders?limit=20`, { headers }),
        axios.get(`${API}/admin/customers`, { headers }),
        axios.get(`${API}/admin/newsletter`, { headers }),
        axios.get(`${API}/admin/drafts`, { headers }),
      ]);
      setStats(s.data);
      setOrders(o.data.orders || []);
      setCustomers(c.data.customers || []);
      setNewsletter(n.data.subscribers || []);
      setDrafts(d.data.drafts || []);
    } catch (err) {
      if (err.response?.status === 401) handleAuthError();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const logout = () => {
    localStorage.removeItem("mf_admin_token");
    navigate("/admin/login");
  };

  const exportNewsletterCsv = () => {
    const rows = ["email,subscribed_at"].concat(
      newsletter.map((s) => `${s.email},${s.subscribed_at || ""}`)
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nyhedsbrev-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-secondary/20 border-t-secondary animate-spin mx-auto" />
          <p className="text-muted-foreground mt-4">Henter data…</p>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="min-h-screen bg-muted/20 py-6 md:py-10" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">
              Admin Dashboard
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              MinFesttale.dk · {new Date().toLocaleDateString("da-DK")}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchAll}
              className="inline-flex items-center gap-2 text-sm bg-white border border-border hover:border-primary px-3 py-2 rounded-lg transition-colors"
              data-testid="admin-refresh"
            >
              <RefreshCw className="h-4 w-4" />
              Opdater
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-red-600 bg-white border border-border hover:border-red-300 px-3 py-2 rounded-lg transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" />
              Log ud
            </button>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-6">
          <StatCard icon={TrendingUp} label="I dag" value={formatDkk(stats.today.revenue)} sub={`${stats.today.orders} ordrer`} color="green" />
          <StatCard icon={TrendingUp} label="Sidste 7 dage" value={formatDkk(stats.week_7d.revenue)} sub={`${stats.week_7d.orders} ordrer`} color="secondary" />
          <StatCard icon={TrendingUp} label="Sidste 30 dage" value={formatDkk(stats.month_30d.revenue)} sub={`${stats.month_30d.orders} ordrer`} color="primary" />
          <StatCard icon={Package} label="Total omsætning" value={formatDkk(stats.all_time.revenue)} sub={`${stats.all_time.orders} ordrer`} color="blue" />
          <StatCard icon={Users} label="Unikke kunder" value={stats.customer_count} color="primary" />
          <StatCard icon={Package} label="Gns. ordrestørrelse" value={formatDkk(stats.avg_order_dkk)} color="secondary" />
          <StatCard icon={CheckCircle} label="Konvertering" value={`${stats.conversion_rate_pct}%`} sub="Drafts → betalt" color="green" />
          <StatCard icon={Mail} label="Nyhedsbrev" value={stats.newsletter_count} color="pink" />
        </div>

        {/* Chart */}
        <div className="mb-6">
          <Chart data={stats.chart_14d} />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-border/40 shadow-sm overflow-hidden">
          <div className="flex border-b border-border/40 overflow-x-auto">
            {[
              { id: "orders", label: `Seneste ordrer (${orders.length})`, icon: Package },
              { id: "customers", label: `Kunder (${customers.length})`, icon: Users },
              { id: "newsletter", label: `Nyhedsbrev (${newsletter.length})`, icon: Mail },
              { id: "drafts", label: `Drafts (${drafts.length})`, icon: FileText },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 md:px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  tab === t.id
                    ? "text-primary border-b-2 border-secondary bg-secondary/5"
                    : "text-muted-foreground hover:text-primary"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-4 md:p-6 overflow-x-auto">
            {tab === "orders" && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border/40">
                    <th className="py-2 pr-3">Tidspunkt</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Beløb</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 && (
                    <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">Ingen ordrer endnu</td></tr>
                  )}
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-border/20 hover:bg-muted/20">
                      <td className="py-2 pr-3 text-muted-foreground">{formatDate(o.created_at)}</td>
                      <td className="py-2 pr-3 text-primary">{o.customer_email || "—"}</td>
                      <td className="py-2 pr-3 font-medium text-primary">{formatDkk(o.total_price_dkk)}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                          o.payment_status === "paid"
                            ? "bg-accent/15 text-accent"
                            : "bg-yellow-100 text-yellow-700"
                        }`}>
                          {o.payment_status === "paid" ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                          {o.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tab === "customers" && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border/40">
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Køb</th>
                    <th className="py-2 pr-3">Total brugt</th>
                    <th className="py-2 pr-3">Sidste køb</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length === 0 && (
                    <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">Ingen kunder endnu</td></tr>
                  )}
                  {customers.map((c) => (
                    <tr key={c.email} className="border-b border-border/20 hover:bg-muted/20">
                      <td className="py-2 pr-3 text-primary">{c.email}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{c.order_count}</td>
                      <td className="py-2 pr-3 font-medium text-primary">{formatDkk(c.total_spent_dkk)}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{formatDate(c.last_order)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tab === "newsletter" && (
              <div>
                <div className="flex justify-end mb-3">
                  <button
                    onClick={exportNewsletterCsv}
                    className="text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-full hover:bg-primary/90"
                    data-testid="admin-export-newsletter"
                  >
                    Eksportér som CSV
                  </button>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border/40">
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Tilmeldt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newsletter.length === 0 && (
                      <tr><td colSpan={2} className="py-8 text-center text-muted-foreground">Ingen tilmeldte endnu</td></tr>
                    )}
                    {newsletter.map((s) => (
                      <tr key={s.email} className="border-b border-border/20 hover:bg-muted/20">
                        <td className="py-2 pr-3 text-primary">{s.email}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{formatDate(s.subscribed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "drafts" && (
              <div>
                <p className="text-xs text-muted-foreground mb-3">
                  Potentielle leads: personer der startede formularen men ikke har gennemført køb.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border/40">
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Anledning</th>
                      <th className="py-2 pr-3">Til</th>
                      <th className="py-2 pr-3">Step</th>
                      <th className="py-2 pr-3">Opdateret</th>
                      <th className="py-2 pr-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drafts.length === 0 && (
                      <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Ingen drafts</td></tr>
                    )}
                    {drafts.map((d) => (
                      <tr key={d.email} className="border-b border-border/20 hover:bg-muted/20">
                        <td className="py-2 pr-3 text-primary">{d.email || "—"}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{d.occasion || "—"}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{d.recipient_name || "—"}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{d.current_step}/4</td>
                        <td className="py-2 pr-3 text-muted-foreground">{formatDate(d.updated_at)}</td>
                        <td className="py-2 pr-3">
                          {d.has_purchased ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-accent/15 text-accent">
                              <CheckCircle className="h-3 w-3" /> Købt
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                              <Clock className="h-3 w-3" /> Lead
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
